import React, { useState, useEffect, useMemo } from 'react';
import {
  Search, FileText, ChevronLeft, ChevronRight, Filter,
  Download, Trash2, LayoutList, Database, File, CheckCircle, 
  Edit, X, Calendar, Percent
} from 'lucide-react';
import axios from 'axios';
import BeelineSubmissionForm from '../Vendorfiles/BeelineSubmissionForm'; // Adjust path if it's in another folder!
import './AdminDashboard.css';

export default function AppliedDatabase() {
  const tableScrollRef = React.useRef(null);
  const [applications, setApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- VIEW TOGGLE STATE ---
  const [showBeeline, setShowBeeline] = useState(false);

  // --- EDIT MODAL STATE ---
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  
  // NEW: State for Beeline Full-Screen Edit
  const [showBeelineEditModal, setShowBeelineEditModal] = useState(false);
  const [beelineEditData, setBeelineEditData] = useState(null);

  const handleBeelineEditClick = (app) => {
    setBeelineEditData(app);
    setShowBeelineEditModal(true);
  };

  // --- FILTER MODAL STATE ---
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterCriteria, setFilterCriteria] = useState({
    scoreMin: 0,
    scoreMax: 100,
    dateStart: '',
    dateEnd: '',
    selectedRecruiters: [],
    selectedVendors: [],
    selectedRequestIds: [] // <-- Added for Beeline
  });
    
  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  useEffect(() => {
    fetchApplications();
  }, []);

  // Mouse-drag-to-scroll for the horizontally scrollable table.
  // Uses Pointer Events and only activates for an actual MOUSE pointer —
  // touch pointers are completely ignored so real finger swipes (and
  // DevTools' touch emulation) are left to the browser's native scroll,
  // exactly like RegisteredDatabase.jsx already does successfully.
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let scrollLeftStart = 0;

    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse') return; // never intercept touch
      isDown = true;
      el.classList.add('dragging');
      startX = e.clientX;
      scrollLeftStart = el.scrollLeft;
    };
    const onPointerUpOrLeave = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      isDown = false;
      el.classList.remove('dragging');
    };
    const onPointerMove = (e) => {
      if (e.pointerType !== 'mouse' || !isDown) return;
      e.preventDefault();
      const walk = e.clientX - startX;
      el.scrollLeft = scrollLeftStart - walk;
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointerup', onPointerUpOrLeave);
    el.addEventListener('pointerleave', onPointerUpOrLeave);
    el.addEventListener('pointermove', onPointerMove);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointerup', onPointerUpOrLeave);
      el.removeEventListener('pointerleave', onPointerUpOrLeave);
      el.removeEventListener('pointermove', onPointerMove);
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showBeeline, filterCriteria]);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/all-applications?t=${new Date().getTime()}`);
      setApplications(response.data);
    } catch (error) {
      console.error("Error fetching applications", error);
    }
  };

  // --- EXTRACT UNIQUE FILTER OPTIONS (CONTEXT AWARE) ---
  const uniqueRecruiters = useMemo(() => {
    return [...new Set(applications
      .filter(app => showBeeline ? (app.beelineId || app.requestId || app.resourceTypeId) : !(app.beelineId || app.requestId || app.resourceTypeId))
      .filter(app => app.recruiter)
      .map(app => app.recruiter.username || app.recruiter.recruiterName)
    )].filter(Boolean);
  }, [applications, showBeeline]);

  const uniqueVendors = useMemo(() => {
    return [...new Set(applications
      .filter(app => showBeeline ? (app.beelineId || app.requestId || app.resourceTypeId) : !(app.beelineId || app.requestId || app.resourceTypeId))
      .filter(app => app.vendor)
      .map(app => app.vendor.vendorName)
    )].filter(Boolean);
  }, [applications, showBeeline]);

  const uniqueRequestIds = useMemo(() => {
    if (!showBeeline) return [];
    return [...new Set(applications
      .filter(app => app.requestId)
      .map(app => app.requestId)
    )].filter(Boolean);
  }, [applications, showBeeline]);

  // --- FILTER HANDLERS ---
  const handleCheckboxChange = (type, value) => {
    setFilterCriteria(prev => {
      const list = prev[type];
      if (list.includes(value)) {
        return { ...prev, [type]: list.filter(item => item !== value) };
      } else {
        return { ...prev, [type]: [...list, value] };
      }
    });
  };

  const clearFilters = () => {
    setFilterCriteria({
      scoreMin: 0, scoreMax: 100,
      dateStart: '',
      dateEnd: '',
      selectedRecruiters: [],
      selectedVendors: [],
      selectedRequestIds: []
    });
  };

  // --- HANDLE STATUS UPDATE ---
  const handleStatusUpdate = async (appId, newStatus) => {
    try {
        setApplications(prev => prev.map(app =>
            app.id === appId ? { ...app, candidateStatus: newStatus } : app
        ));
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/${appId}`, {
            candidateStatus: newStatus
        });
    } catch (error) {
        console.error("Error updating status:", error);
        alert("Failed to update status.");
        fetchApplications();
    }
  };

  // --- HANDLE EDIT ---
  const handleEditClick = (app) => {
    setEditFormData({ ...app });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
        const targetId = editFormData.id || editFormData._id;
        if (!targetId) { alert("Error: Cannot find candidate ID."); return; }
        const { job, recruiter, vendor, ...submitData } = editFormData;
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/${targetId}`, submitData);
        setApplications(prev => prev.map(app => 
            (app.id || app._id) === targetId ? { ...app, ...submitData } : app
        ));
        setShowEditModal(false);
        alert("Application updated successfully.");
        fetchApplications();
    } catch (error) {
        console.error("Error updating application:", error);
        alert("Failed to update application.");
    }
  };

  const handleDelete = async (appOrId) => {
    if (!window.confirm("Are you sure you want to delete this application?")) return;
    
    try {
      // This line makes it bulletproof! 
      const targetId = typeof appOrId === 'object' ? (appOrId.id || appOrId._id) : appOrId;

      if (!targetId) {
          console.error("No ID found to delete!");
          return;
      }

      // Route it to the correct backend API based on the current view
      if (showBeeline) {
          await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/beeline/submission/${targetId}`);
      } else {
          await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/delete-application/${targetId}`);
      }

      // Instantly remove it from the table UI
      setApplications(prev => prev.filter(item => (item.id || item._id) !== targetId));
      alert("Application deleted successfully.");
      
    } catch (error) {
      console.error("Error deleting application:", error);
      alert("Failed to delete application.");
    }
  };

  const handleExport = () => {
    if (currentApps.length === 0) { alert("No data to export"); return; }

    if (showBeeline) {
      // ── BEELINE EXPORT ──────────────────────────────────────────────
      const headers = [
        "Submitted On",
        "Candidate Name",
        "Submitted By",
        "Request ID",
        "Beeline ID",
        "Email",
        "Mobile",
        "DOB",
        "Gender",
        "Nationality",
        "Resource Type",
        "Initial Personal ID",
        "Full Personal ID",
        "LinkedIn",
        "Total Experience",
        "Req Skill Experience",
        "Highest Degree",
        "Specialization",
        "Recent Company",
        "Company Address",
        "Education Start",
        "Education End",
        "Candidate Address",
        "City",
        "State",
        "Pincode",
        "Submitted Rate",
        "Pay Rate",
        "Availability",
        "Date Available",
        "Former TCS Employee",
        "Former Business Associate",
        "Status"
      ];

      const csvRows = [
        headers.join(','),
        ...currentApps.map(app => [
          `"${formatDate(app.submittedAt || app.createdAt)}"`,
          `"${app.candidateName || ((app.firstName || '') + ' ' + (app.lastName || '')).trim() || ''}"`,
          `"${getSource(app)}"`,
          `"${app.requestId || ''}"`,
          `"${app.beelineId || ''}"`,
          `"${app.email || app.candidateEmail || ''}"`,
          `"${app.mobile || app.contactNumber || ''}"`,
          `"${formatDate(app.dob)}"`,
          `"${app.gender || ''}"`,
          `"${app.nationality || ''}"`,
          `"${app.resourceTypeId || ''}"`,
          `"${app.initialPersonalId || ''}"`,
          `"${app.fullPersonalId || ''}"`,
          `"${app.linkedInLink || ''}"`,
          `"${app.totalExperience || app.totalITExperience || ''}"`,
          `"${app.experienceRequiredSkills || ''}"`,
          `"${app.highestDegree || ''}"`,
          `"${app.specialization || ''}"`,
          `"${app.currentCompanyName || app.recentCompany || ''}"`,
          `"${app.recentCompanyAddress || ''}"`,
          `"${formatDate(app.educationStartDate)}"`,
          `"${formatDate(app.educationEndDate)}"`,
          `"${app.candidateAddress || ''}"`,
          `"${app.candidateCity || ''}"`,
          `"${app.candidateState || ''}"`,
          `"${app.candidatePincode || ''}"`,
          `"${app.submittedRate || ''}"`,
          `"${app.submittedPayRate || ''}"`,
          `"${app.availability || ''}"`,
          `"${formatDate(app.dateAvailable)}"`,
          `"${app.tcsFormerEmployee || ''}"`,
          `"${app.tcsFormerBusinessAssociate || ''}"`,
          `"${app.candidateStatus || ''}"`
        ].join(','))
      ];

      const csvData = csvRows.join('\n');
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', 'beeline_export.csv');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } else {
      // ── STANDARD EXPORT ─────────────────────────────────────────────
      const headers = [
        "Uploaded Date",
        "Candidate Name",
        "Email",
        "Contact",
        "Applied For (Job)",
        "Company Name",
        "Source",
        "Status",
        "Match %",
        "Profile Uploaded",
        "Total Experience",
        "Relevant Experience",
        "Skills",
        "Education",
        "Current Company",
        "Client Name",
        "Current CTC",
        "Expected CTC",
        "Notice Period",
        "Currently Working",
        "Last Working Day",
        "Reason for Change",
        "Current Location",
        "Preferred Location",
        "Comments"
      ];

      const csvRows = [
        headers.join(','),
        ...currentApps.map(app => [
          `"${formatDate(app.submittedAt)}"`,
          `"${app.candidateName || ''}"`,
          `"${app.candidateEmail || ''}"`,
          `"${app.contactNumber || ''}"`,
          `"${app.job?.role || ''}"`,
          `"${app.job?.companyName || ''}"`,
          `"${getSource(app)}"`,
          `"${app.candidateStatus || ''}"`,
          `"${app.matchScore || 0}"`,
          `"${app.profileUploaded || ''}"`,
          `"${app.totalExperience || ''}"`,
          `"${app.relevantExperience || ''}"`,
          `"${(app.skillSet || '').replace(/"/g, "'")}"`,
          `"${(app.education || '').replace(/"/g, "'")}"`,
          `"${app.currentCompanyName || ''}"`,
          `"${app.clientName || ''}"`,
          `"${app.currentCTC || ''}"`,
          `"${app.expectedCTC || ''}"`,
          `"${app.noticePeriod || ''}"`,
          `"${app.currentlyWorking || ''}"`,
          `"${formatDate(app.lastWorkingDay)}"`,
          `"${(app.reasonForChange || '').replace(/"/g, "'")}"`,
          `"${app.currentLocation || ''}"`,
          `"${app.preferredLocation || ''}"`,
          `"${(app.comments || '').replace(/"/g, "'")}"`
        ].join(','))
      ];

      const csvData = csvRows.join('\n');
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', 'applications_export.csv');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // --- FILTER LOGIC ---
  const filteredApps = applications.filter(app => {
    // 1. View Filtering
    const isBeelineApp = app.beelineId || app.requestId || app.resourceTypeId;
    if (showBeeline && !isBeelineApp) return false;
    if (!showBeeline && isBeelineApp) return false;

    // 2. Search Logic
    const term = searchTerm.toLowerCase();
    const name = app.candidateName || (app.firstName ? `${app.firstName} ${app.lastName}` : '') || '';
    const role = app.job?.role || '';
    const company = app.job?.companyName || '';
    const beelineReq = app.requestId || '';
    const matchesSearch = name.toLowerCase().includes(term) ||
                          role.toLowerCase().includes(term) ||
                          company.toLowerCase().includes(term) ||
                          beelineReq.toLowerCase().includes(term);
    
    if (!matchesSearch) return false;

    // 3. ADVANCED FILTERS
    
    // Match Score (Only applies to Standard Apps)
    if (!showBeeline) {
        const score = app.matchScore || 0;
        if (score < filterCriteria.scoreMin || score > filterCriteria.scoreMax) return false;
    }

    // Request IDs (Only applies to Beeline Apps)
    if (showBeeline && filterCriteria.selectedRequestIds.length > 0) {
        if (!app.requestId || !filterCriteria.selectedRequestIds.includes(app.requestId)) return false;
    }

    // Date Range
    if (filterCriteria.dateStart || filterCriteria.dateEnd) {
        const appDate = new Date(app.submittedAt || app.createdAt).setHours(0,0,0,0);
        if (filterCriteria.dateStart) {
            const startDate = new Date(filterCriteria.dateStart).setHours(0,0,0,0);
            if (appDate < startDate) return false;
        }
        if (filterCriteria.dateEnd) {
            const endDate = new Date(filterCriteria.dateEnd).setHours(23,59,59,999);
            if (appDate > endDate) return false;
        }
    }

    // Recruiters
    if (filterCriteria.selectedRecruiters.length > 0) {
        const recName = app.recruiter?.username || app.recruiter?.recruiterName;
        if (!recName || !filterCriteria.selectedRecruiters.includes(recName)) return false;
    }

    // Vendors
    if (filterCriteria.selectedVendors.length > 0) {
        const vendName = app.vendor?.vendorName;
        if (!vendName || !filterCriteria.selectedVendors.includes(vendName)) return false;
    }

    return true;
  });

  // --- SORT & PAGINATION ---
  const sortedApps = [...filteredApps].sort((a, b) => {
     return new Date(b.submittedAt || b.createdAt) - new Date(a.submittedAt || a.createdAt);
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentApps = sortedApps.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedApps.length / itemsPerPage);

  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };

  const getSource = (app) => {
    // 1. Check for Recruiter Name first
    if (app.recruiter?.recruiterName) return `Recruiter: ${app.recruiter.recruiterName}`;
    if (app.recruiter?.username) return `Recruiter: ${app.recruiter.username}`;
    
        // 3. Check for Vendor Name
    if (app.vendor?.vendorName) return `Vendor: ${app.vendor.vendorName}`;
    
    // 3. Check mapped IDs from the backend controller
    if (app.recruiterId?.recruiterName) return `Recruiter: ${app.recruiterId.recruiterName}`;
    if (app.vendorId?.vendorName) return `Vendor: ${app.vendorId.vendorName}`;

    // 3b. Direct candidate-portal applications
    if (app.candidateUser?.fullName || app.candidateUser?.username) {
      return `Candidate Portal: ${app.candidateUser.fullName || app.candidateUser.username}`;
    }
    if (app.candidateUserId) return 'Candidate Portal';
    
    // 4. Fallback to Beeline submittedBy if it exists and isn't just "Admin"
    if (app.submittedBy && app.submittedBy !== 'Admin') return app.submittedBy;

    return 'Direct / Admin';
  };

  const getFreshFileUrl = (url) => {
    if (!url) return '#';
    // Directs the browser to your backend to fetch a fresh token before opening the PDF
    return `${process.env.REACT_APP_API_BASE_URL}/api/candidates/view-file?url=${encodeURIComponent(url)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  const displayVal = (val) => val || '--';

  return (
    <div className="content-card" style={{
        display: 'flex', flexDirection: 'column',
        height: 'calc(100vh - 120px)', width: '100%',
        minWidth: 0,
        overflow: 'hidden', background: '#fff',
        borderRadius: '16px', padding: '20px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e2e8f0'
    }}>
      
      {/* HEADER SECTION */}
      <div className="section-header" style={{
          display:'flex', justifyContent:'space-between', marginBottom:'15px', alignItems:'center', flexShrink: 0, minWidth: 0, flexWrap: 'wrap', gap: '10px'
      }}>
        <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
            <h3 style={{margin:0, fontSize:'1.25rem', color:'#1e293b'}}>
                {showBeeline ? "Beeline Submissions" : "All Job Applicants"}
            </h3>
            
            <button
                onClick={() => setShowBeeline(!showBeeline)}
                style={{
                    display:'flex', alignItems:'center', gap:'8px', padding:'6px 12px',
                    borderRadius:'20px', border:'1px solid #e2e8f0', cursor:'pointer',
                    background: showBeeline ? '#eff6ff' : '#f8fafc',
                    color: showBeeline ? '#2563eb' : '#64748b',
                    fontWeight: '600', transition: 'all 0.2s'
                }}
            >
                {showBeeline ? <LayoutList size={16}/> : <Database size={16}/>}
                {showBeeline ? "Switch to Standard View" : "Switch to Beeline View"}
            </button>
        </div>
        
        <div style={{display:'flex', gap:'10px'}}>
            <button
                onClick={handleExport}
                style={{
                    display:'flex', alignItems:'center', gap:'8px',
                    padding:'10px 16px', borderRadius:'6px', border:'none',
                    background:'#10b981', color:'white', cursor:'pointer', fontWeight:'600'
                }}
            >
                <Download size={16} /> Export to Excel
            </button>

            {/* FILTER BUTTON */}
            <button 
                onClick={() => setShowFilterModal(true)}
                style={{
                    display:'flex', alignItems:'center', gap:'8px',
                    padding:'10px 16px', borderRadius:'6px', border:'1px solid #e2e8f0',
                    background:'white', color:'#1e293b', cursor:'pointer', fontWeight:'600',
                    position:'relative'
                }}
            >
                <Filter size={16} color={ (filterCriteria.selectedVendors.length > 0 || filterCriteria.selectedRecruiters.length > 0 || filterCriteria.dateStart) ? "#2563eb" : "#64748b"} />
                Filter
                {(filterCriteria.selectedVendors.length > 0 || filterCriteria.selectedRecruiters.length > 0 || filterCriteria.dateStart || filterCriteria.scoreMin > 0) && (
                    <span style={{
                        position:'absolute', top:'-4px', right:'-4px', width:'10px', height:'10px',
                        background:'#ef4444', borderRadius:'50%', border:'2px solid white'
                    }}></span>
                )}
            </button>

            <div style={{position:'relative'}}>
                <input
                    type="text"
                    placeholder={showBeeline ? "Search Name, Request ID..." : "Search Applicant, Role, Company..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{padding:'10px 35px 10px 12px', borderRadius:'6px', border:'1px solid #e2e8f0', width:'100%', maxWidth:'300px', minWidth:0}}
                />
                <Search size={18} style={{position:'absolute', right:'10px', top:'10px', color:'#94a3b8'}} />
            </div>
        </div>
      </div>

      {/* TABLE CONTAINER */}
      <div ref={tableScrollRef} className="table-container" style={{
          overflow: 'auto', flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', position:'relative', WebkitOverflowScrolling: 'touch'
      }}>
        <table className="modern-table" style={{width: showBeeline ? '4500px' : '3200px', borderCollapse: 'separate', borderSpacing: 0}}>
          <thead style={{position: 'sticky', top: 0, zIndex: 30}}>
            <tr style={{background:'#f8fafc'}}>
              {showBeeline ? (
                <>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0', position:'sticky', left:0, background:'#f8fafc', zIndex:40}}>Submitted On</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0', position:'sticky', left:'120px', background:'#f8fafc', zIndex:40, borderRight:'2px solid #e2e8f0'}}>Candidate Name</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Submitted By</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Request ID</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Beeline ID</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Email</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Mobile</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>DOB</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Gender</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Nationality</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Resource Type</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Initial ID</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Full Personal ID</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>LinkedIn</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Total Exp</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Req Skill Exp</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Highest Degree</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Specialization</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Recent Company</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Company Address</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Edu Start</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Edu End</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Cand Address</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>City</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>State</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Pincode</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0', color:'#059669'}}>Sub. Rate</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Pay Rate</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Available?</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Date Avail</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Former TCS?</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Former BA?</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Attachments</th>
                </>
              ) : (
                <>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Uploaded Date</th>
                  <th className="sticky-name-col" style={{
                      position:'sticky', left:0, background:'#f8fafc', zIndex:40,
                      padding:'12px', borderBottom:'1px solid #e2e8f0', borderRight:'2px solid #e2e8f0', minWidth:'180px'
                  }}>
                      Candidate Name
                  </th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Email</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Contact</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Applied For (Job)</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Company Name</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Source</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0', width: '220px', minWidth: '220px'}}>Status</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Match %</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Profile Uploaded</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Total Exp</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Relevant Exp</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Skills</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Education</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Current Company</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Client Name</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Current CTC</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Expected CTC</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Notice Period</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Currently Working?</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Last Working Day</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Reason for Change</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Current Location</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Preferred Loc</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Resume</th>
                  <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Comments</th>
                </>
              )}
              <th className="sticky-actions-col" style={{
                  position:'sticky', right:0, background:'#f8fafc', zIndex:40, textAlign:'center',
                  padding:'12px', borderBottom:'1px solid #e2e8f0', borderLeft:'2px solid #e2e8f0'
              }}>
                  Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {currentApps.length > 0 ? (
                currentApps.map(app => (
              <tr key={app.id} style={{background:'#fff', borderBottom:'1px solid #f1f5f9'}}>
                {showBeeline ? (
                  <>
                    <td style={{padding:'12px', position:'sticky', left:0, background:'white', zIndex:10}}>{formatDate(app.submittedAt || app.createdAt)}</td>
                    <td style={{padding:'12px', fontWeight:'600', position:'sticky', left:'120px', background:'white', zIndex:10, borderRight:'2px solid #e2e8f0'}}>
                        {displayVal(app.candidateName || `${app.firstName || ''} ${app.lastName || ''}`)}
                    </td>
                    <td style={{padding:'12px', background:'#f0f9ff'}}>{getSource(app)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.requestId)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.beelineId)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.email || app.candidateEmail)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.mobile || app.contactNumber)}</td>
                    <td style={{padding:'12px'}}>{formatDate(app.dob)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.gender)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.nationality)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.resourceTypeId)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.initialPersonalId)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.fullPersonalId)}</td>
                    <td style={{padding:'12px'}}>{app.linkedInLink ? <a href={app.linkedInLink} target="_blank" rel="noreferrer">Profile</a> : '--'}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.totalExperience || app.totalITExperience)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.experienceRequiredSkills)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.highestDegree)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.specialization)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.currentCompanyName || app.recentCompany)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.recentCompanyAddress)}</td>
                    <td style={{padding:'12px'}}>{formatDate(app.educationStartDate)}</td>
                    <td style={{padding:'12px'}}>{formatDate(app.educationEndDate)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.candidateAddress)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.candidateCity)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.candidateState)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.candidatePincode)}</td>
                    <td style={{padding:'12px', color:'#059669', fontWeight:'600'}}>${displayVal(app.submittedRate)}</td>
                    <td style={{padding:'12px'}}>${displayVal(app.submittedPayRate)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.availability)}</td>
                    <td style={{padding:'12px'}}>{formatDate(app.dateAvailable)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.tcsFormerEmployee)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.tcsFormerBusinessAssociate)}</td>
                    <td style={{padding:'12px'}}>
                        <div style={{display:'flex', gap:'8px'}}>
                            {app.resumeFile?.fileUrl && <a href={getFreshFileUrl(app.resumeFile.fileUrl)} target="_blank" rel="noreferrer" title="Original Resume"><FileText size={18} color="#64748b" /></a>}
                            {app.originalResumeUrl && <a href={getFreshFileUrl(app.originalResumeUrl)} target="_blank" rel="noreferrer" title="Original Resume"><FileText size={18} color="#64748b" /></a>}
                            {app.modifiedResumeUrl && <a href={getFreshFileUrl(app.modifiedResumeUrl)} target="_blank" rel="noreferrer" title="Modified Resume"><File size={18} color="#2563eb" /></a>}
                            {app.attestationPdfUrl && <a href={getFreshFileUrl(app.attestationPdfUrl)} target="_blank" rel="noreferrer" title="Attestation"><CheckCircle size={18} color="#059669" /></a>}
                        </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{padding:'12px', color:'#64748b'}}>{formatDate(app.submittedAt)}</td>
                    <td className="sticky-name-col" style={{
                        position:'sticky', left:0, background:'#fff', zIndex:30,
                        fontWeight:'600', borderRight:'2px solid #e2e8f0', color:'#1e293b', padding:'12px'
                    }}>
                        {displayVal(app.candidateName)}
                    </td>
                    <td style={{padding:'12px'}}>{displayVal(app.candidateEmail)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.contactNumber)}</td>
                    <td style={{padding:'12px', color:'#2563eb', fontWeight:'500'}}>{displayVal(app.job?.role)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.job?.companyName)}</td>
                    <td style={{padding:'12px'}}>{getSource(app)}</td>
                    
                    <td style={{padding:'12px'}}>
                        <select
                            className="status-dropdown"
                            value={app.candidateStatus || 'Pending Review'}
                            onChange={(e) => handleStatusUpdate(app.id, e.target.value)}
                            style={{
                                width: '100%', minWidth: '180px', fontSize: '14px', fontWeight: '600',
                                padding: '10px 12px', borderRadius: '6px', cursor: 'pointer',
                                border: '1px solid #cbd5e1',
                                backgroundColor: app.candidateStatus?.includes('Reject') ? '#fef2f2' : app.candidateStatus?.includes('Select') ? '#f0fdf4' : 'white',
                                color: app.candidateStatus?.includes('Reject') ? '#b91c1c' : app.candidateStatus?.includes('Select') ? '#15803d' : '#334155'
                            }}
                        >
                            <option value="Pending Review">Pending Review</option>
                            <optgroup label="Screening">
                                <option value="Screening - Select">Screening - Select</option>
                                <option value="Screening - Reject">Screening - Reject</option>
                            </optgroup>
                            <optgroup label="Assessment">
                                <option value="Assessment - Select">Assessment - Select</option>
                                <option value="Assessment - Reject">Assessment - Reject</option>
                            </optgroup>
                            <optgroup label="Bot Review">
                                <option value="Bot Review - Select">Bot Review - Select</option>
                                <option value="Bot Review - Reject">Bot Review - Reject</option>
                            </optgroup>
                            <optgroup label="Level 1">
                                <option value="L1 - Select">L1 - Select</option>
                                <option value="L1 - Reject">L1 - Reject</option>
                            </optgroup>
                            <optgroup label="Level 2">
                                <option value="L2 - Select">L2 - Select</option>
                                <option value="L2 - Reject">L2 - Reject</option>
                            </optgroup>
                            <optgroup label="Level 3">
                                <option value="L3 - Select">L3 - Select</option>
                                <option value="L3 - Reject">L3 - Reject</option>
                            </optgroup>
                            <optgroup label="Level 4">
                                <option value="L4 - Select">L4 - Select</option>
                                <option value="L4 - Reject">L4 - Reject</option>
                            </optgroup>
                            <optgroup label="Final">
                                <option value="Final Select">Final Select</option>
                                <option value="Final Reject">Final Reject</option>
                                <option value="On hold">On hold</option>
                            </optgroup>
                        </select>
                    </td>

                    <td style={{padding:'12px'}}><span style={{fontWeight:'bold', color: app.matchScore > 70 ? '#16a34a' : '#d97706'}}>{app.matchScore}%</span></td>
                    <td style={{padding:'12px'}}>{displayVal(app.profileUploaded)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.totalExperience)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.relevantExperience)}</td>
                    <td style={{padding:'12px'}}><div style={{maxWidth:'250px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}} title={app.skillSet}>{displayVal(app.skillSet)}</div></td>
                    <td style={{padding:'12px'}}><div style={{maxWidth:'200px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{displayVal(app.education)}</div></td>
                    <td style={{padding:'12px'}}>{displayVal(app.currentCompanyName)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.clientName)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.currentCTC)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.expectedCTC)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.noticePeriod)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.currentlyWorking)}</td>
                    <td style={{padding:'12px'}}>{formatDate(app.lastWorkingDay)}</td>
                    <td style={{padding:'12px'}}><div style={{maxWidth:'200px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{displayVal(app.reasonForChange)}</div></td>
                    <td style={{padding:'12px'}}>{displayVal(app.currentLocation)}</td>
                    <td style={{padding:'12px'}}>{displayVal(app.preferredLocation)}</td>
                    <td style={{padding:'12px'}}>
                      {app.resumeFile && app.resumeFile.fileUrl ? (
                        <a href={getFreshFileUrl(app.resumeFile.fileUrl)} target="_blank" rel="noreferrer" style={{display:'flex', alignItems:'center', gap:'5px', color:'#2563eb'}}>
                           <FileText size={16} /> PDF
                        </a>
                      ) : <span style={{color:'#cbd5e1'}}>No File</span>}
                    </td>
                    <td style={{padding:'12px'}}>{displayVal(app.comments)}</td>
                  </>
                )}
                
                <td className="sticky-actions-col" style={{
                    position:'sticky', right:0, background:'#fff', zIndex:30,
                    borderLeft:'2px solid #e2e8f0', textAlign:'center', padding:'12px'
                }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                            onClick={() => showBeeline ? handleBeelineEditClick(app) : handleEditClick(app)}
                            style={{
                                background: '#e0f2fe', border: 'none', cursor: 'pointer',
                                color: '#2563eb', padding: '6px', borderRadius: '4px',
                                transition: 'background 0.2s'
                            }}
                            title={showBeeline ? "Re-open Submission Wizard" : "Edit Application"}
                        >
                            <Edit size={16} />
                        </button>
                        <button
                            onClick={() => handleDelete(app)}
                            style={{
                                background: '#fee2e2', border: 'none', cursor: 'pointer',
                                color: '#ef4444', padding: '6px', borderRadius: '4px',
                                transition: 'background 0.2s'
                            }}
                            title="Delete Application"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                </td>
              </tr>
            ))
            ) : (
              <tr>
                <td colSpan="40" style={{textAlign:'center', padding:'30px', color:'#94a3b8'}}>No applications found for this view.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* PAGINATION */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'15px 0 0 0', borderTop:'1px solid #e2e8f0', flexShrink: 0 }}>
          <span style={{color:'#64748b', fontSize:'0.9rem'}}>Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedApps.length)} of {sortedApps.length}</span>
          <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
              <button onClick={handlePrevPage} disabled={currentPage === 1} style={{ padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:'6px', background: currentPage === 1 ? '#f1f5f9' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                  <ChevronLeft size={16} /> Previous
              </button>
              <span style={{color: '#1e293b', fontWeight: '500'}}>Page {currentPage} of {totalPages || 1}</span>
              <button onClick={handleNextPage} disabled={currentPage >= totalPages} style={{ padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:'6px', background: currentPage >= totalPages ? '#f1f5f9' : '#fff', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                  Next <ChevronRight size={16} />
              </button>
          </div>
      </div>

      {/* --- FILTER MODAL --- */}
      {showFilterModal && (
        <div style={{
            position:'fixed', top:0, left:0, width:'100%', height:'100%', 
            backgroundColor:'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', 
            display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000
        }}>
          <div style={{
              background:'white', width:'650px', maxWidth:'95%', maxHeight:'90vh',
              borderRadius:'16px', display:'flex', flexDirection:'column',
              boxShadow:'0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow:'hidden',
              animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>
            <div style={{padding:'20px 24px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <h3 style={{margin:0, fontSize:'1.25rem', color:'#0f172a', fontWeight:'700'}}>Filter Applicants</h3>
              <button onClick={() => setShowFilterModal(false)} style={{background:'none', border:'none', cursor:'pointer', color:'#94a3b8'}}><X size={20} /></button>
            </div>
            <div style={{padding:'24px', overflowY:'auto'}}>
              {!showBeeline && (
                <div style={{marginBottom:'24px'}}>
                  <label style={{display:'flex', alignItems:'center', gap:'8px', fontWeight:'600', color:'#334155', marginBottom:'10px', fontSize:'0.95rem'}}>
                      <Percent size={14} color="#2563eb"/> Match Score Range
                  </label>
                  <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <div style={{flex:1}}>
                      <span style={{display:'block', fontSize:'0.75rem', color:'#64748b', marginBottom:'6px'}}>Min %</span>
                      <input type="number" min="0" max="100" value={filterCriteria.scoreMin} onChange={(e) => setFilterCriteria({...filterCriteria, scoreMin: e.target.value})}
                          style={{width:'100%', padding:'10px 12px', borderRadius:'8px', border:'1px solid #e2e8f0', textAlign:'center', fontSize:'0.9rem', outline:'none', boxSizing:'border-box'}} />
                    </div>
                    <span style={{color:'#94a3b8', fontWeight:'bold', paddingTop:'20px'}}>-</span>
                    <div style={{flex:1}}>
                      <span style={{display:'block', fontSize:'0.75rem', color:'#64748b', marginBottom:'6px'}}>Max %</span>
                      <input type="number" min="0" max="100" value={filterCriteria.scoreMax} onChange={(e) => setFilterCriteria({...filterCriteria, scoreMax: e.target.value})}
                          style={{width:'100%', padding:'10px 12px', borderRadius:'8px', border:'1px solid #e2e8f0', textAlign:'center', fontSize:'0.9rem', outline:'none', boxSizing:'border-box'}} />
                    </div>
                  </div>
                </div>
              )}
              {showBeeline && (
                <div style={{marginBottom:'24px'}}>
                  <label style={{display:'flex', alignItems:'center', gap:'8px', fontWeight:'600', color:'#334155', marginBottom:'10px', fontSize:'0.95rem'}}>
                      <FileText size={14} color="#2563eb"/> By Request ID
                  </label>
                  <div style={{background:'white', border:'1px solid #e2e8f0', borderRadius:'8px', maxHeight:'120px', overflowY:'auto', padding:'12px'}}>
                    {uniqueRequestIds.length > 0 ? uniqueRequestIds.map(reqId => (
                      <label key={reqId} style={{display:'flex', alignItems:'center', padding:'8px 4px', cursor:'pointer', transition:'0.1s', userSelect:'none'}}>
                        <input type="checkbox" checked={filterCriteria.selectedRequestIds.includes(reqId)} onChange={() => handleCheckboxChange('selectedRequestIds', reqId)}
                            style={{marginRight:'12px', width:'16px', height:'16px', accentColor:'#2563eb', cursor:'pointer'}} />
                        <span style={{fontSize:'0.9rem', color:'#475569'}}>{reqId}</span>
                      </label>
                    )) : <div style={{textAlign:'center', padding:'10px', color:'#94a3b8', fontSize:'0.85rem'}}>No Request IDs found</div>}
                  </div>
                </div>
              )}
              <div style={{marginBottom:'24px'}}>
                <label style={{display:'flex', alignItems:'center', gap:'8px', fontWeight:'600', color:'#334155', marginBottom:'10px', fontSize:'0.95rem'}}>
                    <Calendar size={14} color="#2563eb"/> Application Date
                </label>
                <div style={{display:'flex', gap:'12px', alignItems:'center'}}>
                  <input type="date" value={filterCriteria.dateStart} onChange={(e) => setFilterCriteria({...filterCriteria, dateStart: e.target.value})}
                    style={{flex:1, padding:'10px 12px', border:'1px solid #e2e8f0', borderRadius:'8px', color:'#1e293b', outline:'none', boxSizing:'border-box'}} />
                  <span style={{color:'#94a3b8', fontSize:'0.9rem'}}>to</span>
                  <input type="date" value={filterCriteria.dateEnd} onChange={(e) => setFilterCriteria({...filterCriteria, dateEnd: e.target.value})}
                    style={{flex:1, padding:'10px 12px', border:'1px solid #e2e8f0', borderRadius:'8px', color:'#1e293b', outline:'none', boxSizing:'border-box'}} />
                </div>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px'}}>
                <div>
                  <label style={{display:'block', fontWeight:'600', color:'#334155', marginBottom:'10px', fontSize:'0.95rem'}}>By Recruiter</label>
                  <div style={{background:'white', border:'1px solid #e2e8f0', borderRadius:'8px', height:'200px', overflowY:'auto', padding:'12px'}}>
                    {uniqueRecruiters.length > 0 ? uniqueRecruiters.map(rec => (
                      <label key={rec} style={{display:'flex', alignItems:'center', padding:'8px 4px', cursor:'pointer', transition:'0.1s', userSelect:'none'}}>
                        <input type="checkbox" checked={filterCriteria.selectedRecruiters.includes(rec)} onChange={() => handleCheckboxChange('selectedRecruiters', rec)}
                            style={{marginRight:'12px', width:'16px', height:'16px', accentColor:'#2563eb', cursor:'pointer'}} />
                        <span style={{fontSize:'0.9rem', color:'#475569'}}>{rec}</span>
                      </label>
                    )) : <div style={{textAlign:'center', padding:'20px', color:'#94a3b8', fontSize:'0.85rem'}}>No recruiters found</div>}
                  </div>
                </div>
                <div>
                  <label style={{display:'block', fontWeight:'600', color:'#334155', marginBottom:'10px', fontSize:'0.95rem'}}>By Vendor</label>
                  <div style={{background:'white', border:'1px solid #e2e8f0', borderRadius:'8px', height:'200px', overflowY:'auto', padding:'12px'}}>
                    {uniqueVendors.length > 0 ? uniqueVendors.map(ven => (
                      <label key={ven} style={{display:'flex', alignItems:'center', padding:'8px 4px', cursor:'pointer', transition:'0.1s', userSelect:'none'}}>
                        <input type="checkbox" checked={filterCriteria.selectedVendors.includes(ven)} onChange={() => handleCheckboxChange('selectedVendors', ven)}
                            style={{marginRight:'12px', width:'16px', height:'16px', accentColor:'#2563eb', cursor:'pointer'}} />
                        <span style={{fontSize:'0.9rem', color:'#475569'}}>{ven}</span>
                      </label>
                    )) : <div style={{textAlign:'center', padding:'20px', color:'#94a3b8', fontSize:'0.85rem'}}>No vendors found</div>}
                  </div>
                </div>
              </div>
            </div>
            <div style={{padding:'20px 30px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'flex-end', gap:'16px', background:'white'}}>
              <button onClick={clearFilters} style={{background:'none', border:'none', color:'#64748b', fontWeight:'600', cursor:'pointer', fontSize:'0.95rem'}}>Reset</button>
              <button onClick={() => setShowFilterModal(false)} style={{background:'#2563eb', color:'white', border:'none', padding:'10px 28px', borderRadius:'8px', fontWeight:'600', cursor:'pointer', fontSize:'0.95rem', boxShadow:'0 2px 4px rgba(37,99,235,0.2)'}}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- EXISTING EDIT MODAL (STANDARD) --- */}
      {showEditModal && editFormData && (
        <div className="bd-modal-overlay" style={{
            position:'fixed', top:0, left:0, width:'100%', height:'100%', 
            backgroundColor:'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', 
            display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000
        }}>
            <div className="bd-modal-content" style={{ 
                maxWidth: '800px', width: '90%', maxHeight: '90vh', overflowY: 'auto', 
                background:'white', borderRadius:'12px', padding:'30px', 
                boxShadow:'0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' 
            }}>
                <div className="bd-modal-header" style={{
                    borderBottom:'2px solid #f1f5f9', paddingBottom:'15px', marginBottom:'25px', 
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                    <h2 style={{margin:0, fontSize:'1.5rem', color:'#0f172a', fontWeight:'700'}}>Edit Candidate Profile</h2>
                    <button onClick={() => setShowEditModal(false)} style={{
                        background: '#f1f5f9', border: 'none', cursor: 'pointer', 
                        color:'#64748b', borderRadius: '50%', padding: '8px', display: 'flex'
                    }}>
                        <X size={20}/>
                    </button>
                </div>
                
                <form onSubmit={handleEditSubmit}>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'25px'}}>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Candidate Name</label>
                            <input type="text" name="candidateName" value={editFormData.candidateName || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Email Address</label>
                            <input type="email" name="candidateEmail" value={editFormData.candidateEmail || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Contact Number</label>
                            <input type="text" name="contactNumber" value={editFormData.contactNumber || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Total Experience</label>
                            <input type="text" name="totalExperience" value={editFormData.totalExperience || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Relevant Experience</label>
                            <input type="text" name="relevantExperience" value={editFormData.relevantExperience || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Current CTC</label>
                            <input type="text" name="currentCTC" value={editFormData.currentCTC || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Expected CTC</label>
                            <input type="text" name="expectedCTC" value={editFormData.expectedCTC || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Notice Period</label>
                            <input type="text" name="noticePeriod" value={editFormData.noticePeriod || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Current Location</label>
                            <input type="text" name="currentLocation" value={editFormData.currentLocation || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>
                        <div>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Preferred Location</label>
                            <input type="text" name="preferredLocation" value={editFormData.preferredLocation || ''} onChange={handleEditChange} 
                                style={{width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none'}} />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{display:'block', fontSize:'0.85rem', color:'#475569', fontWeight:'600', marginBottom:'6px'}}>Comments / Notes</label>
                            <textarea 
                                name="comments" 
                                value={editFormData.comments || ''} 
                                onChange={handleEditChange} 
                                rows="3"
                                placeholder="Add any interview feedback or internal notes here..."
                                style={{
                                    width:'100%', padding:'10px 12px', borderRadius:'6px', border:'1px solid #cbd5e1', 
                                    background:'#f8fafc', color:'#1e293b', boxSizing:'border-box', outline:'none', 
                                    resize:'vertical', fontFamily:'inherit'
                                }} 
                            />
                        </div>
                    </div>

                    <div style={{display:'flex', justifyContent:'flex-end', gap:'12px', borderTop:'2px solid #f1f5f9', paddingTop:'20px'}}>
                        <button type="button" onClick={() => setShowEditModal(false)} 
                            style={{padding:'10px 20px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'white', color:'#475569', cursor:'pointer', fontWeight:'600', transition:'0.2s'}}>
                            Cancel
                        </button>
                        <button type="submit" 
                            style={{padding:'10px 24px', borderRadius:'6px', border:'none', background:'#2563eb', color:'white', cursor:'pointer', fontWeight:'600', boxShadow:'0 4px 6px -1px rgba(37, 99, 235, 0.2)', transition:'0.2s'}}>
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* --- BEELINE EDIT FULL-SCREEN MODAL (NOW SAFELY AT THE BOTTOM) --- */}
      {showBeelineEditModal && beelineEditData && (
        <div style={{
            position:'fixed', top:0, left:0, width:'100vw', height:'100vh',
            backgroundColor:'#f8fafc', zIndex: 9999, overflowY: 'auto'
        }}>
            <BeelineSubmissionForm 
                initialData={beelineEditData}
                // We pass a mock 'job' and 'partner' so the form doesn't crash 
                // and keeps the original vendor's name intact!
                job={{ id: beelineEditData.jobId }}
                partner={{ 
                    id: beelineEditData.vendorId || beelineEditData.recruiterId,
                    vendorName: beelineEditData.vendor?.vendorName,
                    recruiterName: beelineEditData.recruiter?.recruiterName || beelineEditData.recruiter?.username,
                    role: beelineEditData.vendorId ? 'vendor' : 'recruiter'
                }}
                onBack={() => {
                    setShowBeelineEditModal(false);
                    setBeelineEditData(null);
                    fetchApplications(); // Instantly refresh the table to show updates
                }}
            />
        </div>
      )}

    </div>
  );
}